import React, { useEffect, useState } from 'react'
import { ArrowUp } from 'lucide-react'
import MainNavbar from '../../components/Landing_page_Navbar/index'
import {Link} from 'react-router-dom'
import './style.css'
const QUADRANTS = [
  {
    code: 'B2B',
    color: 'navy',
    title: 'B2B  Business to Business',
    text: 'Businesses can easily find and connect with other businesses for products, services, suppliers, contractors, manpower, technology, partnerships, and other resources',
  },
  {
    code: 'B2C',
    color: 'teal',
    title: 'B2C  Business to Consumer',
    text: 'Businesses can reach consumers who are looking for their products, services, and business opportunities',
  },
  {
    code: 'C2C',
    color: 'navy',
    title: 'C2C  Consumer to Consumer',
    text: 'Consumers and individuals can connect to exchange, provide, promote, or sell products, services, skills, and opportunities',
  },
  {
    code: 'C2B',
    color: 'navy',
    title: 'C2B  Consumer to Business',
    text: 'Individuals can offer their skills, services, products, ideas, resources, or business opportunities directly to companies that need them',
  },
];

const SIMPLE_CONCEPT = [
  { bold: 'Business needs a resource', rest: 'SELECTEZI helps find it' },
  { bold: 'A person has a resource', rest: 'SELECTEZI helps find someone who needs it' },
  { bold: 'A business has an opportunity', rest: 'SELECTEZI helps find the right people' },
  { bold: 'A consumer needs a product/service', rest: 'SELECTEZI helps find the right business' },
];

const OPPORTUNITIES = [
  { icon: '🏪', title: 'Retail Business', text: 'opportunities to become a retailer or distributor' },
  { icon: '📦', title: 'Wholesale Business', text: 'opportunities to buy and distribute products at wholesale level' },
  { icon: '🏭', title: 'Manufacturing', text: 'opportunities to manufacture products or establish small scale production' },
  { icon: '🌾', title: 'Agriculture & Farming', text: 'farming, agricultural products, livestock, and related business opportunities' },
  { icon: '🐄', title: 'Animals & Livestock', text: 'buying, selling, breeding, farming, and related resources' },
  { icon: '🏗️', title: 'Real Estate', text: 'properties, construction related opportunities, investment and services' },
  { icon: '🚚', title: 'Transportation & Logistics', text: 'transport providers, vehicles, logistics services and opportunities' },
  { icon: '🛠️', title: 'Service Providers', text: 'individuals and companies offering professional, technical, domestic, and business services' },
  { icon: '🏠', title: 'Home Based Businesses', text: 'people can produce and sell homemade food, handicrafts, clothing, products and other items' },
  { icon: '💎', title: 'Gems & Jewelry', text: 'producers, craftsmen, wholesalers, retailers, and buyers can connect' },
  { icon: '🛍️', title: 'Products & Trading', text: 'individuals can discover products they can sell or distribute' },
  { icon: '👨‍💼', title: 'Jobs & Employment', text: 'companies can publish available jobs and individuals can find employment opportunities' },
  { icon: '🚀', title: 'Business Opportunities', text: 'companies and individuals can publish opportunities for partnerships, distribution, dealership, investment, services, and other forms of collaboration' },
];

const BIGGER_VISION = [
  { lead: 'A farmer has agricultural products', flow: 'SELECTEZI helps him find buyers' },
  { lead: 'A manufacturer has products', flow: 'SELECTEZI helps find distributors and retailers' },
  { lead: 'A person wants to start a business', flow: 'SELECTEZI helps them find products, suppliers and opportunities' },
  { lead: 'A person has a skill', flow: 'SELECTEZI helps them find customers or companies that need that skill' },
  { lead: 'A company needs manpower', flow: 'SELECTEZI helps it find suitable people' },
  { lead: 'A person wants a job', flow: 'SELECTEZI helps them discover available jobs' },
];
const BIGGER_VISION_FULL = {
  lead: 'A homemaker produces handmade products',
  flow: 'SELECTEZI gives her a channel to find customers and grow into a business',
};

const PHILOSOPHY = [
  { icon: '😊', text: 'Every Person Has Potential' },
  { icon: '🗄️', text: 'Every Resource Has Value' },
  { icon: '🏢', text: 'Every Business Has Needs' },
  { icon: '💡', text: 'Every Need Can Create an Opportunity' },
  { icon: '🚀', text: 'Every Opportunity Can Become a Business' },
];

const PHASE1_TAGS = [
  'Skills', 'Experience', 'Available capital', 'Location', 'Products',
  'Land', 'Equipment', 'Vehicles', 'Manufacturing capability', 'Professional services',
  'Agricultural resources', 'Handmade products', 'Business interests', 'Available time', 'Existing business network',
];

const PHASE2_BUSINESS_FINDS = [
  'Suppliers', 'Manufacturers', 'Distributors', 'Retailers', 'Wholesalers',
  'Raw material providers', 'Transporters', 'Service providers', 'Contractors', 'Employees',
  'Consultants', 'Business partners', 'Customers',
];
const PHASE2_INDIVIDUAL_FINDS = [
  'Businesses', 'Customers', 'Suppliers', 'Employment opportunities', 'Business partners',
  'Products to sell', 'Services to provide', 'Distribution opportunities', 'Manufacturing opportunities',
  'Investment and collaboration opportunities',
];

const PHASE4_GROUPS = [
  {
    label: 'Products:',
    items: ['Manufactured products', 'Agricultural products', 'Raw materials', 'Handmade products', 'Gems', 'Jewelry', 'Livestock related products', 'Consumer goods', 'Industrial products'],
  },
  {
    label: 'Services:',
    items: ['Professional services', 'Technical services', 'Transportation', 'Construction services', 'Maintenance', 'Consulting', 'Home services', 'Business services'],
  },
  {
    label: 'Skills:',
    items: ['A person can offer their knowledge, experience or technical ability to businesses or consumers'],
  },
  {
    label: 'Business Resources:',
    items: ['Raw materials', 'Machinery', 'Equipment', 'Vehicles', 'Production capacity', 'Warehousing', 'Distribution', 'Manpower', 'Professional expertise'],
  },
];

const PILLAR1_ITEMS = [
  'Discover products they can sell', 'Find suppliers and manufacturers', 'Find wholesale and retail opportunities',
  'Discover dealership and distribution opportunities', 'Find available jobs and employment opportunities', 'Offer their professional or technical skills',
  'Offer transportation services', 'Sell agricultural and farming products', 'Sell livestock and animal related products',
  'Offer homemade products', 'Sell handicrafts, gems and jewelry', 'Find customers for their services',
  'Find businesses looking for their skills or resources', 'Discover potential business partners', 'Identify opportunities available in their local or wider market',
];
const PILLAR1_BENEFITS_INDIVIDUAL = ['Discover new income opportunities', 'Identify business ideas', 'Monetize existing skills and resources', 'Find customers and markets', 'Find suppliers and products', 'Discover jobs and business opportunities'];
const PILLAR1_BENEFITS_BUSINESS = ['Find new suppliers', 'Find skilled people', 'Find distributors and retailers', 'Find service providers', 'Find new customers', 'Discover new markets and business opportunities'];

const PILLAR2_BENEFITS = ['Products', 'Raw materials', 'Suppliers', 'Manufacturers', 'Customers', 'Distributors', 'Retailers', 'Wholesalers', 'Transporters', 'Service providers', 'Skilled workers', 'Business partners', 'Employment', 'Markets', 'Business opportunities'];

const PILLAR3_FLOWS = [
  'A small retailer could become: Retailer → Multiple Retailers → Wholesaler → Distributor',
  'A small producer could become: Home Producer → Local Producer → Manufacturer → Brand',
  'A service provider could become: Individual → Freelancer → Service Provider → Company',
  'A farmer could become: Farmer → Producer → Supplier → Distributor → Agricultural Business',
];
const PILLAR3_BENEFITS = ['Find more customers', 'Find new suppliers', 'Expand into new markets', 'Find distributors and retailers', 'Source raw materials', 'Find transportation', 'Find skilled employees', 'Outsource services', 'Discover partnerships', 'Promote products and services', 'Identify new business opportunities', 'Build relationships with other businesses', 'Expand from local to wider markets'];

const TRANSFORMATION_LADDER = [
  'Consumer', 'Opportunity Seeker', 'Resource Provider', 'Seller / Service Provider',
  'Entrepreneur', 'Business Owner', 'Retailer / Wholesaler / Producer / Manufacturer', 'Growing Business',
];

const CONNECTED_LINES = [
  'A retailer needs a wholesaler', 'A wholesaler needs a manufacturer', 'A manufacturer needs raw materials',
  'A farmer produces resources', 'A transporter moves them', 'A service provider supports them',
  'A property provides the place', 'A job company provides people', 'A food business creates products',
  'A consumer needs products and services',
];

const CATEGORIES = [
  {
    num: '01', title: 'RETAIL',
    question: 'Are You a Retailer? Turn Your Shop Into a Digital Showcase',
    intro: 'Whether you operate a small neighborhood shop, supermarket, showroom, fashion store, electronics shop, spare parts shop or any other retail business, SELECTEZI gives your business a place to be discovered.',
    label: 'SHOWCASE WHAT YOU SELL: CREATE YOUR RETAIL SHOP SHOWCASE AND LET CUSTOMERS DISCOVER:',
    tags: ['Your products', 'Your brands', 'Your shop', 'Your location', 'Your offers', 'Your services', 'Your business info'],
    afterTags: "But your customers aren't your only opportunity. Your retail business also needs:",
    flows: ['Products → Wholesalers → Manufacturers → Suppliers → Transportation → Employees → Services → Property → Customers'],
    bullets: [
      'Looking for products? Find wholesalers, manufacturers and suppliers',
      'Looking for customers? Make your shop and products discoverable',
      'Looking for transportation? Find transportation resources',
      'Looking for employees? Discover people and job opportunities',
      'Looking for another shop or warehouse? Explore property opportunities',
      'Want to expand? Find new products, suppliers, customers, partners and markets',
    ],
    closing: 'Your shop is more than a place to sell. It is a business that needs an entire network of resources. Create your Retail Showcase on SELECTEZI.',
  },
  {
    num: '02', title: 'WHOLESALE',
    question: 'Are You a Wholesaler? Connect Your Supply With More Businesses',
    intro: "A wholesaler doesn't only need buyers. You need manufacturers, suppliers, products, warehouses, transportation, retailers, distributors and business opportunities. SELECTEZI brings these resources closer together.",
    label: 'SHOWCASE YOUR WHOLESALE BUSINESS: CREATE YOUR WHOLESALE SHOP SHOWCASE AND LET BUSINESSES DISCOVER:',
    tags: ['What you supply', 'Products and brands', 'Wholesale availability', 'Business location', 'Areas you serve', 'Business information', 'Supply capabilities'],
    flows: [
      'Build your business network: Manufacturers → Wholesaler → Retailers → Businesses → Customers',
      'But the connection can work in every direction. You can discover: Suppliers → Products → Manufacturers → Retailers → Distributors → Transportation → Property → Employees → Services',
    ],
    afterFlows: 'Find more buyers. Find more suppliers. Find new products. Enter new markets. Build new business relationships. Grow your wholesale operation.',
    closing: "Don't just sell products. Build your supply network. Create your Wholesale Showcase on SELECTEZI.",
  },
  {
    num: '03', title: 'MANUFACTURER',
    question: 'Are You a Manufacturer? Find the Resources Behind Your Production',
    intro: 'Every manufacturer needs much more than customers. You need: Raw Materials → Machinery → Equipment → Technology → Employees → Packaging → Transportation → Warehousing → Buyers. SELECTEZI helps bring these business resources into one discoverable system.',
    label: 'SHOWCASE YOUR MANUFACTURING BUSINESS: CREATE YOUR MANUFACTURER SHOP SHOWCASE AND LET BUSINESSES DISCOVER:',
    tags: ['What you manufacture', 'Your products', 'Supply capacity', 'Location', 'Business information', 'Manufacturing capabilities', 'Production categories', 'Customized production'],
    bullets: [
      'Need raw materials? Discover raw material producers and suppliers',
      'Need machinery or equipment? Find businesses providing what your production needs',
      'Need employees? Connect with people offering the skills you require',
      'Need transportation? Find transportation resources',
      'Need buyers? Reach wholesalers, retailers, distributors and businesses',
      'Want to expand? Discover new markets, suppliers, products and business connections',
    ],
    closing: 'From Raw Material to Finished Product. From Manufacturer to Market. SELECTEZI helps connect both sides. Create your Manufacturer Showcase on SELECTEZI.',
  },
  {
    num: '04', title: 'RAW MATERIAL',
    question: "Do You Produce or Supply Raw Materials? Your Resource Can Become Someone's Business",
    intro: "What you produce may be exactly what another business needs. A farmer may need a manufacturer. A manufacturer may need raw materials. A food producer may need agricultural products. A construction company may need construction materials. A processor may need your production. Your resource can be someone else's opportunity.",
    label: 'CREATE YOUR RAW MATERIAL SHOWCASE: LET BUSINESSES DISCOVER:',
    tags: ['What you produce', 'What you supply', 'Quantity/capacity', 'Product category', 'Location', 'Supply information', 'Business details'],
    flows: ['Find Your Market: Raw Material Producer → Manufacturer → Wholesaler → Distributor → Retailer → Customer'],
    afterFlows: 'SELECTEZI helps you become visible across this network.',
    bullets: [
      'Looking for buyers? Find manufacturers, wholesalers and businesses',
      'Looking for equipment? Find machinery and equipment providers',
      'Need transportation? Find transportation resources',
      'Need storage? Discover property and storage opportunities',
      'Want to increase production? Find the resources and connections needed to grow',
    ],
    closing: 'What you produce is not just a product. It is a resource for another business. Turn your resource into opportunity with SELECTEZI.',
  },
  {
    num: '05', title: 'GEMS & JEWELRY',
    question: 'Do You Create, Make, Sell or Trade Gems & Jewelry?',
    intro: 'Whether you are a jeweler, gemstone producer, artisan, manufacturer, retailer, wholesaler or individual seller, SELECTEZI gives you a place to showcase what you offer.',
    label: 'CREATE YOUR GEMS & JEWELRY SHOP SHOWCASE: SHOWCASE:',
    tags: ['Jewelry', 'Gems', 'Handmade pieces', 'Traditional products', 'Precious/decorative', 'Collections', 'Custom work', 'Business information'],
    flows: [
      'And discover more than customers. Find: Suppliers → Gemstone Resources → Manufacturers → Artisans → Packaging → Transportation → Retailers → Wholesalers → Customers',
      'Grow from individual seller to business: Create → Showcase → Connect → Sell → Build Your Brand → Grow',
    ],
    closing: 'Your skills, products and resources can reach people who are looking for them. What you create could be exactly what someone is searching for. Create your Gems & Jewelry Showcase on SELECTEZI.',
  },
  {
    num: '06', title: 'SECOND HAND / USED ITEMS',
    question: 'Have Something You No Longer Need? Someone Else May Need It',
    intro: 'A used phone. Furniture. Equipment. Machinery. Electronics. Tools. Office equipment. Home appliances. Almost anything useful can have a second life.',
    afterIntro: 'Create your Second Hand / Used Item Showcase and make it discoverable. Turn unused resources into opportunities.',
    flows: ['You Have It → Someone Needs It → Connect → Exchange → Value'],
    afterFlows: 'Businesses can also use SELECTEZI to find:',
    tags: ['Used machinery', 'Equipment', 'Office items', 'Tools', 'Furniture', 'Spare resources', 'Business assets'],
    closing: 'Why leave useful things unused? What is unused by you may be valuable to someone else. Show it. Find someone who needs it. Give it a second life on SELECTEZI.',
  },
  {
    num: '07', title: 'HOMEMADE',
    question: 'Make Something at Home? Turn Your Skill Into an Opportunity',
    intro: "Maybe you make: Food, Clothes, Crafts, Gifts, Decorations, Handmade products, Beauty products, Traditional products, Jewelry, Home products. You don't need to start with a big factory. You can start with what you already know how to make.",
    label: 'CREATE YOUR HOMEMADE SHOP SHOWCASE:',
    flows: [
      'Show people: What you make → What you offer → How to contact you → Where you operate',
      'Your journey can begin small: Skill → Product → Showcase → Customer → Income → Business → Growth',
    ],
    afterFirstFlow: 'Then discover customers, suppliers, transportation, packaging resources and other business opportunities.',
    closing: 'Your home can be the beginning of a business. Turn what you make into an opportunity with SELECTEZI.',
  },
  {
    num: '08', title: 'FARMING',
    question: 'Are You a Farmer or Agricultural Producer? Connect What You Produce With the Market',
    intro: 'Farmers produce resources that many businesses need. But finding the right buyers, suppliers, transportation, equipment and services can be difficult. SELECTEZI brings these possibilities together.',
    label: 'CREATE YOUR FARMING SHOWCASE: SHOWCASE:',
    tags: ['Crops', 'Fruits & vegetables', 'Livestock', 'Agricultural products', 'Seeds', 'Farm products', 'Production capabilities', 'Farm resources'],
    flows: [
      'Discover what your farm needs: Seeds → Equipment → Machinery → Workers → Transportation → Storage → Buyers',
      'Discover who needs what you produce: Farmer → Producer → Supplier → Wholesaler → Business → Market',
      'Grow beyond production. You may discover opportunities to become: Producer → Supplier → Processor → Wholesaler → Agricultural Business',
    ],
    closing: 'Your farm is not only producing food. Your farm is producing resources and opportunities. Create your Farming Showcase on SELECTEZI.',
  },
  {
    num: '09', title: 'RECYCLE',
    question: 'Have Recyclable Materials? Turn Waste Into a Resource',
    intro: 'What looks like waste to one person may be a valuable resource for another business. Recyclable materials can move into: Collection → Sorting → Processing → Manufacturing → New Products.',
    afterIntro: 'Create your Recycle Showcase and connect your recyclable resources with businesses that need them. Find:',
    tags: ['Recycling businesses', 'Manufacturers', 'Scrap buyers', 'Material processors', 'Transportation', 'Equipment', 'Business partners', 'Customers'],
    flows: ['Create value from resources that would otherwise be wasted: Waste → Resource → Processing → Product → Business'],
    closing: "Don't just throw it away. Someone may be looking for it. Connect recyclable resources with opportunity on SELECTEZI.",
  },
  {
    num: '10', title: 'USED VEHICLE',
    question: 'Selling a Used Vehicle? Or Looking for One?',
    intro: 'Cars, trucks, buses, vans, motorcycles, heavy equipment and other used vehicles can connect buyers and sellers through SELECTEZI.',
    label: 'CREATE YOUR USED VEHICLE SHOWCASE: SHOW:',
    tags: ['Vehicle type', 'Condition', 'Model', 'Location', 'Photos/details', 'Price information', 'Seller information'],
    flows: [
      'But SELECTEZI can go beyond buying and selling. Vehicle owners can discover: Transportation Opportunities → Drivers → Maintenance → Spare Parts → Services → Customers → Business Opportunities',
      'A vehicle can become more than an asset. It can become: Vehicle → Transportation Service → Customers → Income → Business',
    ],
    closing: 'Your vehicle may be an asset waiting for its next opportunity. Find it. Showcase it. Connect it.',
  },
  {
    num: '11', title: 'SERVICE',
    question: 'Do You Provide a Service? Let People Find You',
    introLabel: 'YOU MAY BE:',
    tags: ['Electrician', 'Plumber', 'Mechanic', 'Engineer', 'Accountant', 'IT specialist', 'Designer', 'Consultant', 'Repair specialist', 'Marketing professional', 'Cleaning company', 'Security provider', 'Legal/professional', 'Construction', 'Technical service', 'Freelancer'],
    flows: [
      'Showcase: What you do → Where you work → Who you serve → What you offer → How people can reach you',
      'Your skill can become: Skill → Service → Customer → Income → Business → Team → Growth',
    ],
    afterFlows: 'Businesses need services every day. And service providers need businesses and customers. SELECTEZI connects both sides.',
    closing: 'Your skill is a resource. Your service is an opportunity. Create your Service Profile on SELECTEZI.',
  },
  {
    num: '12', title: 'TRANSPORTATION',
    question: 'Do You Provide Transportation? Turn Your Vehicle or Fleet Into Business',
    introLabel: 'WHETHER YOU HAVE:',
    tags: ['Truck', 'Trailer', 'Pickup', 'Van', 'Bus', 'Car', 'Heavy transport', 'Equipment transport', 'Logistics service', 'Delivery service'],
    afterTags: 'you have a resource that businesses and people need.',
    flows: [
      'Find opportunities from: Manufacturers → Wholesalers → Retailers → Farmers → Construction → Businesses → Individuals',
      'Your transportation resource can become: Vehicle → Transport Job → Customer → Regular Work → Contract → Fleet → Transportation Business',
    ],
    bullets: [
      'Need drivers? Find people',
      'Need vehicles? Discover used vehicles',
      'Need maintenance? Find service providers',
      'Need customers? Make your transportation business discoverable',
    ],
    closing: "Don't let your vehicle sit idle when someone needs transportation. Connect your transportation resource with opportunity on SELECTEZI.",
  },
  {
    num: '13', title: 'PROPERTY',
    question: 'Do You Have Property? Someone May Be Looking for It',
    introLabel: "PROPERTY ISN'T ONLY ABOUT BUYING AND SELLING. PEOPLE AND BUSINESSES NEED:",
    tags: ['Shops', 'Offices', 'Warehouses', 'Factories', 'Land', 'Farms', 'Showrooms', 'Commercial buildings', 'Storage', 'Rental spaces', 'Business locations'],
    flows: [
      'And businesses can discover property for: Retail → Wholesale → Manufacturing → Farming → Warehousing → Offices → Services',
      'Property can become the starting point of a business: Property → Location → Business → Employees → Customers → Revenue → Growth',
    ],
    closing: "The right property can become someone's next business. Showcase your property on SELECTEZI.",
  },
  {
    num: '14', title: 'JOB COMPANY',
    question: 'Are You a Company Looking for People?',
    intro: 'Businesses need people to grow. Create your Job Company Profile and make your organization discoverable to people looking for opportunities.',
    label: 'SHOWCASE:',
    tags: ['Company', 'Industry', 'Location', 'Careers', 'Job opportunities', 'Required skills', 'Business info'],
    flows: [
      'Connect: Company → Job Opportunity → Candidate → Employee → Business Growth',
      "And SELECTEZI isn't only useful for employers. A person can discover: Job → Skill → Experience → Income → Career → Business Opportunity",
    ],
    closing: 'Businesses need people. People need opportunities. SELECTEZI helps bring them together.',
  },
  {
    num: '15', title: 'FOOD',
    question: 'Do You Make, Sell or Provide Food? Put Your Food Business Where People Can Discover It',
    introLabel: 'WHETHER YOU ARE:',
    tags: ['Restaurant', 'Cafe', 'Bakery', 'Caterer', 'Food manufacturer', 'Home based producer', 'Food supplier', 'Food retailer', 'Food wholesaler'],
    afterTags: 'create your Food Profile.',
    flows: [
      'Showcase: What you offer → Your location → Your products → Your services → Your business',
      'Your food business also needs resources: Raw Materials → Farmers → Suppliers → Equipment → Employees → Transportation → Property → Customers',
      'And you can become a resource for others: From homemade food to restaurant. From restaurant to brand. From brand to food business.',
    ],
    closing: 'Start where you are Grow with SELECTEZI',
  },
];

const GENERAL_USER_DISCOVERIES = ['I can provide this', 'I can sell this', 'I can make this', 'I have this resource', 'I can provide this service', 'I can start this business'];
const GENERAL_USER_DIFF_FLOW = ['You come looking for something', 'You may discover something you can offer', 'You may discover an opportunity', 'That opportunity can become income', 'That income can become a business'];
const PROMPTS = ['What can I offer?', 'What opportunity can I find?', 'What resource do I have?', 'Who needs what I can provide?', 'What business can I build?'];

const FOOTER_LINKS = ['Ecosystem', 'Journey', 'Transformations', '15 Categories', 'Connections', 'For Everyone'];

/* ==========================================================================
   SMALL REUSABLE PIECES
   ========================================================================== */

const SectionEyebrow = ({ children }) => <span className="sz-eyebrow">{children}</span>;

const Tag = ({ children }) => (
  <div className="col-6 col-md-4 col-lg-3 mb-2">
    <div className="sz-tag">{children}</div>
  </div>
);

const FlowLine = ({ children, bold }) => (
  <p className={bold ? 'sz-flow-bold' : ''}>
    <span className="sz-flow">{children}</span>
  </p>
);

const Bullet = ({ text }) => {
  const parts = text.split('?');
  return (
    <li className="sz-bullet">
      {parts.length > 1 ? (
        <>
          <strong>{parts[0]}?</strong>
          {parts.slice(1).join('?')}
        </>
      ) : (
        text
      )}
    </li>
  );
};

/* ==========================================================================
   SECTION COMPONENTS
   ========================================================================== */

function Hero() {
  return (
    <section className="sz-section-lavender sz-section">
      <div className="container pt-4 pb-4">
       
        <h1 className="sz-hero-title mt-3">SELECTEZI  A Complete<br />Business Resource Ecosystem</h1>
        <p className="sz-hero-lede">
          SELECTEZI is a complete B2B, B2C, C2C, and C2B Business Resource System designed to connect
          businesses, consumers, and independent resource providers in one integrated ecosystem.
        </p>
        <p className="sz-hero-lede">
          SELECTEZI&rsquo;s core purpose is to make business resources, opportunities, products, services,
          customers, and partnerships easier to find and access.
        </p>

        <div className="row mt-4 g-3">
          {QUADRANTS.map((q) => (
            <div className="col-12 col-md-6 col-lg-3" key={q.code}>
              <div className="sz-card">
                <span className={`sz-quadrant-badge ${q.color}`}>{q.code}</span>
                <p className="sz-card-title">{q.title}</p>
                <p className="sz-card-text">{q.text}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function RealValueSection() {
  return (
    <section className="sz-section-white sz-section">
      <div className="container pt-4 pb-4">
        <div className="sz-card-flat">
          <h2 className="sz-section-title">The real value of SELECTEZI</h2>
          <p style={{ color: 'var(--sz-primary-mid)', fontWeight: 700 }}>
            SELECTEZI does not simply help people buy and sell. It helps them FIND RESOURCES AND OPPORTUNITIES
          </p>
          <p className="sz-card-text mb-3 mt-2">
            A business may need a supplier, contractor, employee, consultant, service provider, technology
            partner, distributor, customer, or investor. Instead of searching through disconnected channels,
            SELECTEZI brings these resources together in one ecosystem.
          </p>
          <p className="sz-card-text mb-4">
            Likewise, an individual who has a skill, service, product, idea, business opportunity, or other
            resource can make it visible to businesses and other consumers who may need it.
          </p>

          <SectionEyebrow>Simple concept</SectionEyebrow>
          <div className="row g-3 mb-4">
            {SIMPLE_CONCEPT.map((c, i) => (
              <div className="col-12 col-md-6" key={i}>
                <div className="sz-concept-pill">
                  <strong>{c.bold}</strong> <span className="sz-arrow-text">→ {c.rest}</span>
                </div>
              </div>
            ))}
          </div>

          <div className="sz-quote-box mb-4">
            <SectionEyebrow>In one sentence:</SectionEyebrow>
            <p>
              SELECTEZI is a digital business resource ecosystem that connects businesses and consumers
              with the resources, opportunities, products, services, skills, and partnerships they need 
              across B2B, B2C, C2C and C2B.
            </p>
          </div>

          <p className="sz-card-text mb-4">
            Exactly. This makes the concept of SELECTEZI much broader than a conventional marketplace. The key
            idea is resource discovery + opportunity creation + business enablement.
          </p>

          <div className="row g-3 mb-4">
            <div className="col-12 col-md-6">
              <div className="sz-card">
                <SectionEyebrow>Core statement</SectionEyebrow>
                <p className="sz-card-text">
                  SELECTEZI is not just a marketplace where people buy and sell. It is a complete
                  business resource and opportunity ecosystem where people discover what they can do, what
                  they can offer, who needs it, and what business opportunity they can build from it.
                </p>
              </div>
            </div>
            <div className="col-12 col-md-6">
              <div className="sz-card">
                <SectionEyebrow>Vision statement</SectionEyebrow>
                <p className="sz-card-text">
                  SELECTEZI transforms people from consumers into opportunity seekers, resource
                  providers, entrepreneurs, and business owners by connecting them with the resources, markets,
                  skills, services, jobs, and opportunities they need to start and grow.
                </p>
              </div>
            </div>
          </div>

          <div className="sz-quote-box">
            <SectionEyebrow>A powerful one line description:</SectionEyebrow>
            <p style={{ fontWeight: 500 }}>
              SELECTEZI is a complete B2B, B2C, C2C and C2B Business Resource &amp; Opportunity Ecosystem that
              connects people and businesses with the products, services, skills, jobs, suppliers, customers,
              partnerships and business opportunities they need to start, operate and grow.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function OpportunitiesSection() {
  return (
    <section className="sz-section-lavender sz-section">
      <div className="container pt-4 pb-4">
        <h2 className="sz-section-title">Through SELECTEZI, an individual could discover opportunities such as:</h2>
        <p className="sz-section-sub">
          SELECTEZI is designed to give an individual the opportunity to move from simply being a consumer or
          job seeker into becoming a business owner, retailer, wholesaler, manufacturer, producer, service
          provider, or independent seller.
        </p>
        <div className="row g-3">
          {OPPORTUNITIES.map((o, i) => (
            <div className="col-12 col-md-6 col-lg-3" key={i}>
              <div className="sz-card">
                <span className="sz-icon">{o.icon}</span>
                <p className="sz-card-title">{o.title}</p>
                <p className="sz-card-text">{o.text}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="sz-card-flat mt-4">
          <h3 className="sz-section-title" style={{ fontSize: '1.5rem' }}>The bigger vision</h3>
          <p className="sz-section-sub mb-3">The most important part is that SELECTEZI can help convert resources into economic opportunities.</p>
          <div className="row g-3">
            {BIGGER_VISION.map((v, i) => (
              <div className="col-12 col-md-6 col-lg-4" key={i}>
                <div className="sz-card-lavender h-100">
                  <p className="mb-0" style={{ fontSize: '0.95rem' }}>
                    <strong>{v.lead}</strong> <span className="sz-flow">→ {v.flow}</span>
                  </p>
                </div>
              </div>
            ))}
            <div className="col-12">
              <div className="sz-card-lavender">
                <p className="mb-0" style={{ fontSize: '0.95rem' }}>
                  <strong>{BIGGER_VISION_FULL.lead}</strong> <span className="sz-flow">→ {BIGGER_VISION_FULL.flow}</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function PhilosophySection() {
  return (
    <section className="sz-section-white sz-section-tight">
      <div className="container text-center">
        <SectionEyebrow>The core philosophy of SELECTEZI</SectionEyebrow>
        <div className="row g-3 justify-content-center mt-2">
          {PHILOSOPHY.map((p, i) => (
            <div className="col-6 col-md-4 col-lg" key={i}>
              <div className="sz-philosophy-card">
                <span className="sz-icon">{p.icon}</span>
                <p>{p.text}</p>
              </div>
            </div>
          ))}
        </div>
        <p className="sz-section-sub mx-auto mt-4">SELECTEZI is designed to connect all of these together</p>
      </div>
    </section>
  );
}

/* ---------- Journey / Phases ---------- */

function PhaseWrapper({ number, label, title, statement, children }) {
  return (
    <div className="sz-phase-card">
      <div className="d-flex align-items-center gap-3 mb-2">
        <span className="sz-phase-number">{number}</span>
        <div>
          <span className="sz-phase-label">Phase 0{number}</span>
          <h3 className="sz-phase-title">{title}</h3>
        </div>
      </div>
      <p className="sz-phase-statement">{statement}</p>
      {children}
    </div>
  );
}

function JourneySection() {
  return (
    <section className="sz-section-white sz-section">
      <div className="container">
     
        <h2 className="sz-section-title">
          The SELECTEZI Journey</h2>

          <h2 style={{ color: 'var(--sz-primary-mid)' }} className="sz-section-title">DISCOVER → CONNECT → START → TRADE → GROW</h2>
        
        <p className="sz-section-sub">This is the simplest way to understand the SELECTEZI ecosystem</p>

        {/* PHASE 1  DISCOVER */}
        <PhaseWrapper number={1} title="DISCOVER" statement="Discover what you can offer and what opportunities exist.">
          <p className="sz-card-text">SELECTEZI helps individuals and businesses discover opportunities that may otherwise remain invisible.</p>
          <p className="sz-tag-list-label">A person can explore opportunities based on their:</p>
          <div className="row g-2">
            {PHASE1_TAGS.map((t, i) => <Tag key={i}>{t}</Tag>)}
          </div>
          <p className="sz-card-text mt-3 mb-0">At the same time, they can discover what the market needs.</p>
          <div className="sz-example-box">
            <p><em>For example: Someone wants to start a business but does not know where to begin.</em></p>
            <FlowLine bold>Through SELECTEZI, they may discover: Product → Supplier → Wholesale opportunity → Retail opportunity → Customer</FlowLine>
            <p style={{ fontWeight: 700, color: 'var(--sz-navy)' }}>This turns an ordinary consumer into a potential entrepreneur</p>
          </div>
        </PhaseWrapper>

        {/* PHASE 2  CONNECT */}
        <PhaseWrapper number={2} title="CONNECT" statement="Connect with businesses, consumers, suppliers, customers, employees, partners and resource providers">
          <p className="sz-card-text">Once an opportunity or resource is discovered, SELECTEZI helps the user connect with the people or businesses required to pursue it.</p>
          <div className="row g-3 mt-1">
            <div className="col-12 col-md-6">
              <p className="sz-tag-list-label">A business can find:</p>
              <div className="row g-2">{PHASE2_BUSINESS_FINDS.map((t, i) => <Tag key={i}>{t}</Tag>)}</div>
            </div>
            <div className="col-12 col-md-6">
              <p className="sz-tag-list-label">An individual can find:</p>
              <div className="row g-2">{PHASE2_INDIVIDUAL_FINDS.map((t, i) => <Tag key={i}>{t}</Tag>)}</div>
            </div>
          </div>
          <p className="sz-card-text mt-3 mb-0">The objective is to reduce the distance between I have something and someone needs it.</p>
        </PhaseWrapper>

        {/* PHASE 3  START */}
        <PhaseWrapper number={3} title="START" statement="Start a business using available resources and opportunities">
          <p className="sz-card-text">This is one of the most important aspects of SELECTEZI. A person does not necessarily need to begin with a large company.</p>
          <div className="row g-2 mt-1">
            {[
              'Consumer → Seller → Retailer → Wholesaler → Distributor → Manufacturer',
              'Individual → Service Provider → Business Owner',
              'Farmer → Producer → Supplier → Distributor',
              'Homemaker → Home Based Producer → Seller → Brand Owner',
            ].map((f, i) => (
              <div className="col-12 col-md-6" key={i}>
                <div className="sz-card-lavender h-100"><FlowLine>{f}</FlowLine></div>
              </div>
            ))}
          </div>
          <div className="sz-example-box">
            <p>For example, someone may discover that a particular product has demand in their area. SELECTEZI can help that person identify:</p>
            <FlowLine bold>What product? → Who supplies it? → What is the wholesale opportunity? → Who can transport it? → Who are the potential customers? → How can I sell it?</FlowLine>
            <p style={{ fontWeight: 700, color: 'var(--sz-navy)', margin: 0 }}>The individual now has a pathway to establish a business</p>
          </div>
        </PhaseWrapper>

        {/* PHASE 4  TRADE */}
        <PhaseWrapper number={4} title="TRADE" statement="Trade products, services, skills and resources">
          <p className="sz-card-text">Once people and businesses are connected, they can exchange economic value. This can include:</p>
          <div className="row g-3 mt-1">
            {PHASE4_GROUPS.map((g, i) => (
              <div className="col-12 col-md-6" key={i}>
                <div className="sz-card h-100">
                  <p className="sz-card-title mb-2">{g.label}</p>
                  <ul className="mb-0 ps-3" style={{ fontSize: '0.9rem', color: 'var(--sz-text-muted)' }}>
                    {g.items.map((it, j) => <li key={j}>{it}</li>)}
                  </ul>
                </div>
              </div>
            ))}
          </div>
          <p className="sz-card-text mt-3 mb-0">Therefore, SELECTEZI is not limited to a traditional product marketplace. It creates an environment where products, services, skills, resources and opportunities can find their respective markets.</p>
        </PhaseWrapper>

        {/* PHASE 5  GROW */}
        <PhaseWrapper number={5} title="GROW" statement="Grow from an individual into a retailer, wholesaler, manufacturer, producer or service provider">
          <p className="sz-card-text">SELECTEZI&rsquo;s ultimate purpose is not simply to complete a transaction. It is to help users progress economically. A person may start with one product.</p>
          <div className="row g-2 mt-1">
            {[
              { label: 'Then become:', flow: 'Seller → Retailer → Wholesaler → Distributor → Manufacturer' },
              { label: 'A farmer may progress from:', flow: 'Producer → Local Seller → Supplier → Agricultural Business' },
              { label: 'A homemaker may progress from:', flow: 'Home Based Producer → Seller → Business Owner → Brand' },
              { label: 'A skilled person may progress from:', flow: 'Individual → Freelancer → Service Provider → Company' },
            ].map((f, i) => (
              <div className="col-12 col-md-6" key={i}>
                <div className="sz-card-lavender h-100">
                  <p className="mb-1" style={{ fontSize: '0.82rem', color: 'var(--sz-text-muted)', fontWeight: 600 }}>{f.label}</p>
                  <FlowLine>{f.flow}</FlowLine>
                </div>
              </div>
            ))}
          </div>
          <p className="sz-card-text mt-3 mb-0">This creates a pathway for people to participate in the economy at different levels.</p>
        </PhaseWrapper>
      </div>
    </section>
  );
}

/* ---------- Transformation / Pillars ---------- */

function Pillar1() {
  return (
    <div className="sz-pillar-card">
      <span className="sz-pillar-number">Pillar 01</span>
      <h3 className="sz-pillar-title">1 From Consumer to Opportunity</h3>
      <p className="sz-pillar-subtitle">Discover what you can do, what you have, and what you can become</p>
      <p className="sz-card-text">
        Millions of people consume products and services every day, but many have skills, resources, ideas,
        time, land, equipment, products, knowledge, or capital that could be converted into income. SELECTEZI
        helps users discover these possibilities.
      </p>
      <p className="sz-tag-list-label">With SELECTEZI, a person can:</p>
      <div className="row g-2 mb-3">
        {PILLAR1_ITEMS.map((it, i) => <Tag key={i}>{it}</Tag>)}
      </div>
      <div className="sz-example-box">
        <FlowLine bold>The transformation: Consumer → Opportunity Seeker → Resource Provider</FlowLine>
        <p className="mb-0">
          Instead of simply asking What can I buy? the user begins asking What can I
          offer? Who needs what I have? What opportunity can I take?
          What business can I start?
        </p>
      </div>
      <div className="row g-3 mt-1">
        <div className="col-12 col-md-6">
          <div className="sz-benefits-box h-100">
            <SectionEyebrow>Key benefits for individuals</SectionEyebrow>
            <ul className="mb-0 ps-3" style={{ fontSize: '0.9rem' }}>{PILLAR1_BENEFITS_INDIVIDUAL.map((b, i) => <li key={i}>{b}</li>)}</ul>
          </div>
        </div>
        <div className="col-12 col-md-6">
          <div className="sz-benefits-box h-100">
            <SectionEyebrow>Key benefits for businesses</SectionEyebrow>
            <ul className="mb-0 ps-3" style={{ fontSize: '0.9rem' }}>{PILLAR1_BENEFITS_BUSINESS.map((b, i) => <li key={i}>{b}</li>)}</ul>
          </div>
        </div>
      </div>
    </div>
  );
}

function Pillar2() {
  return (
    <div className="sz-pillar-card">   
      <span className="sz-pillar-number">Pillar 02</span>
      <h3 className="sz-pillar-title">2 From Opportunity to Business</h3>
      <p className="sz-pillar-subtitle">Turn an opportunity into an economic activity</p>
      <p className="sz-card-text">
        Finding an opportunity is only the beginning. SELECTEZI can help connect the different resources
        required to convert that opportunity into an actual business.
      </p> 
      <div className="sz-example-box">
        <p>For example, someone wants to become a retailer. Instead of starting from zero, they can use SELECTEZI to discover</p>
        <FlowLine bold>Product → Manufacturer / Supplier → Wholesale Price → Transportation → Market / Customers → Retail Opportunity → Business</FlowLine>
      </div>
      <div className="row g-2 my-3">
        {[
          'A farmer Farm → Products → Buyers → Distribution → Agricultural Business',
          'A vehicle owner → Vehicle → Transportation Opportunity → Customers → Regular Work → Transport Business',
        ].map((f, i) => (
          <div className="col-12" key={i}><div className="sz-card-lavender"><FlowLine>{f}</FlowLine></div></div>
        ))}
      </div>
      <p className="sz-tag-list-label">Key benefits (users can potentially find):</p>
      <div className="row g-2 mb-3">
        {PILLAR2_BENEFITS.map((b, i) => <Tag key={i}>{b}</Tag>)}
      </div>
      <div className="row g-2 mb-3">
        {[
          'A person with a skill: Skill → Customer → Service → Income → Business',
          'A homemaker: Homemade Product → Customers → Sales → Brand → Business',
        ].map((f, i) => (
          <div className="col-12" key={i}><div className="sz-card-lavender"><FlowLine>{f}</FlowLine></div></div>
        ))}
      </div>
      <div className="sz-example-box">
        <FlowLine bold>The transformation: Opportunity → Resource → Connection → Transaction → Business</FlowLine>
        <p className="mb-0">SELECTEZI therefore becomes more than a place to advertise something. It becomes a business enablement ecosystem.</p>
      </div>
    </div>
  );
}

function Pillar3() {
  return (
    <div className="sz-pillar-card">
      <span className="sz-pillar-number">Pillar 03</span>
      <h3 className="sz-pillar-title">3 From Business to Growth</h3>
      <p className="sz-pillar-subtitle">Help businesses move from small beginnings toward sustainable growth</p>
      <p className="sz-card-text mb-1">Starting a business is not the final objective. A business needs:</p>
      <p style={{ fontWeight: 700, color: 'var(--sz-navy)' }}>Customers + Suppliers + Employees + Services + Distribution + Markets + Partnerships + Resources</p>
      <p className="sz-card-text mb-2">SELECTEZI can help businesses continuously discover and connect with these resources</p>
      <div className="row g-2 mb-3">
        {PILLAR3_FLOWS.map((f, i) => (
          <div className="col-12 col-md-6" key={i}><div className="sz-card-lavender h-100"><FlowLine>{f}</FlowLine></div></div>
        ))}
      </div>
      <p className="sz-tag-list-label">Key benefits for business growth (SELECTEZI can help businesses):</p>
      <div className="row g-2 mb-3">
        {PILLAR3_BENEFITS.map((b, i) => <Tag key={i}>{b}</Tag>)}
      </div>
      <div className="sz-example-box">
        <FlowLine bold>The transformation: Business → Connections → Customers → Resources → Market Expansion → Growth</FlowLine>
      </div>
    </div>
  );
}

function TransformationSection() {
  return (
    <section className="sz-section-lavender sz-section">
      <div className="container pt-4 pb-4">
        <SectionEyebrow>Transformation framework</SectionEyebrow>
        <h2 className="sz-section-title">Detailed 3 Pillar Deep Dive</h2>
        <p className="sz-section-sub">
          Today, many people are consumers because they do not know what opportunities are available, where to
          find resources, whom to approach, what they can offer, or how to start a business. SELECTEZI brings
          these possibilities into one ecosystem.
        </p>

        <Pillar1 />
        <Pillar2 />
        <Pillar3 />

        <div className="sz-card-flat text-center mt-4">
          <h3 className="sz-section-title" style={{ fontSize: '1.4rem' }}>The transformation SELECTEZI wants to create</h3>
          <ul className="sz-ladder my-4">
            {TRANSFORMATION_LADDER.map((step, i) => (
              <React.Fragment key={i}>
                <li>{step}</li>
                {i < TRANSFORMATION_LADDER.length - 1 && <span className="sz-ladder-arrow">↓</span>}
              </React.Fragment>
            ))}
          </ul>
          <p className="sz-section-sub mx-auto mb-0">
            This is what makes SELECTEZI different from a conventional marketplace. A traditional marketplace
            primarily asks: What do you want to buy or sell? SELECTEZI asks: What do you
            have, what do you need, what can you offer, what opportunity are you looking for, and what business
            can you build?
          </p>
        </div>
      </div>
    </section>
  );
}

function ConnectedSection() {
  return (
    <section className="sz-section-white sz-section">
      <div className="container pt-4 pb-4">
        <div className="row justify-content-center text-center">
          <div className="col-lg-8">
            <h2 className="sz-section-title">Everything Is Connected</h2>
            <p className="sz-section-sub mx-auto">
              One system where people and businesses can discover what they need, showcase what they have,
              connect with each other, create opportunities and grow.
            </p>
          </div>
        </div>
        <div className="row justify-content-center">
          <div className="col-lg-7">
            {CONNECTED_LINES.map((l, i) => (
              <p className="sz-connected-line mb-0" key={i}>{l}</p>
            ))}
          </div>
        </div>
        <p className="text-center mt-4 mb-1" style={{ fontWeight: 700, color: 'var(--sz-navy)' }}>
          And every one of them can become a resource for someone else
        </p>
        <p className="text-center" style={{ fontWeight: 800, fontSize: '1.2rem', color: 'var(--sz-primary)' }}>
          That&rsquo;s SELECTEZI
        </p>
        <p className="text-center">
          <span className="sz-badge-pill">Universal Value Exchange &amp; Resource Interconnection</span>
        </p>
      </div>
    </section>
  );
}

/* ---------- 15 Categories ---------- */

function CategoryCard({ c }) {
  return (
    <div className="sz-category-card">
      <div className="sz-category-head">
        <span className="sz-category-number">{c.num}</span>
        <div>
          <h3 className="sz-category-title">{c.num} {c.title}</h3>
          <p className="sz-category-question">{c.question}</p>
        </div>
      </div>

      {c.intro && <p className="sz-card-text">{c.intro}</p>}
      {c.afterIntro && <p className="sz-card-text">{c.afterIntro}</p>}

      {(c.label || c.introLabel) && <p className="sz-category-label">{c.label || c.introLabel}</p>}

      {c.tags && (
        <div className="row g-2 mb-2">
          {c.tags.map((t, i) => <Tag key={i}>{t}</Tag>)}
        </div>
      )}

      {c.afterTags && <p className="sz-card-text">{c.afterTags}</p>}

      {(c.flows || c.bullets) && (
        <div className="sz-flow-box">
          {c.flows && c.flows.map((f, i) => <FlowLine key={i} bold>{f}</FlowLine>)}
          {c.afterFirstFlow && <p style={{ color: 'var(--sz-text-muted)' }}>{c.afterFirstFlow}</p>}
          {c.afterFlows && <p style={{ color: 'var(--sz-text-muted)' }}>{c.afterFlows}</p>}
          {c.bullets && (
            <div className="row">
              {c.bullets.map((b, i) => (
                <div className="col-12 col-md-6" key={i}>
                  <ul className="ps-3 mb-0"><Bullet text={b} /></ul>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <p className="sz-category-closing">{c.closing}</p>
    </div>
  );
}

function CategoriesSection() {
  return (
    <section className="sz-section-lavender sz-section">
      <div className="container pt-4 pb-4">
        <SectionEyebrow>Complete taxonomy directory</SectionEyebrow>
        <h2 className="sz-section-title">The 15 complete business sectors showcase and &amp; Profiles</h2>
        <p className="sz-section-sub">
          Every single category detailed with its full purpose, showcase requirements, interconnected needs,
          and growth trajectories.
        </p>
        {CATEGORIES.map((c) => <CategoryCard c={c} key={c.num} />)}
      </div>
    </section>
  );
}

function GeneralUserSection() {
  return (
    <section className="sz-section-white sz-section">
      <div className="container pt-4 pb-4">
        <SectionEyebrow>Universal access</SectionEyebrow>
        <p style={{ fontWeight: 700, color: 'var(--sz-navy)' }}>You Don&rsquo;t Have to Own a Business to Start Using SELECTEZI</p>

        <p className="sz-card-text mb-1">You can come to SELECTEZI simply because you are looking for:</p>
        <div className="sz-flow-box mb-3">
          <FlowLine bold>A Product → A Service → A Job → A Property → A Vehicle → A Supplier → A Business → A Skill → An Opportunity</FlowLine>
        </div>

        <p className="sz-card-text mb-2">But while searching, you may discover something else:</p>
        <div className="row g-2 mb-3">
          {GENERAL_USER_DISCOVERIES.map((d, i) => (
            <div className="col-6 col-md-4" key={i}><div className="sz-tag justify-content-center">{d}</div></div>
          ))}
        </div>

        <p className="sz-card-text mb-2">And that is where SELECTEZI becomes different.</p>
        <div className="sz-card-lavender mb-4">
          {GENERAL_USER_DIFF_FLOW.map((l, i) => (
            <p className="mb-1" style={{ fontSize: '0.92rem' }} key={i}>{l}</p>
          ))}
        </div>

        <p className="sz-tag-list-label">SELECTEZI encourages people to ask:</p>
        <div className="row g-3">
          {PROMPTS.map((p, i) => (
            <div className="col-6 col-md-4 col-lg" key={i}>
              <div className="sz-prompt-card">
                {p}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FinalCTA() {
  return (
    <section className="sz-cta-band">
      <div className="container">
        <p className="sz-logo-large">SELECTEZI</p>
        <h2>Find What You Need To Buy</h2>
        <h2>Show What You Want To Sell</h2>
        <h2>Discover What You Can Become Tomorrow</h2>
        <p>
          Whether you are looking for a product, service, supplier, customer, employee, property, vehicle,
          business opportunity  or you have something to offer  SELECTEZI brings the right people,
          businesses, products, services and opportunities together.
        </p>
        <div className="d-flex justify-content-center">
          
          <button type="button" className="sz-btn-outline-light" disabled>Explore Opportunities</button>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
     <footer className="selectezi-footer p-3">
  <div className=" d-flex align-items-center justify-content-between flex-wrap gap-2">
    <span>
      &copy; 2025 SELECTEZI Inc. All rights reserved.
    </span>

    <div className="d-flex align-items-center gap-3">
      <Link to="/general-terms">General Terms</Link>
      <Link to="/privacy-policy">Privacy Policy</Link>
    </div>
  </div>
</footer>
  );
}

/* ==========================================================================
   PAGE
   ========================================================================== */

export default function SelectziLandingPage() {
  const [showBackToTop, setShowBackToTop] = useState(false);

  useEffect(() => {
    const updateBackToTopVisibility = () => {
      setShowBackToTop(window.scrollY > 320);
    };

    updateBackToTopVisibility();
    window.addEventListener('scroll', updateBackToTopVisibility, { passive: true });
    return () => window.removeEventListener('scroll', updateBackToTopVisibility);
  }, []);

  return (
    <div className="sz-root">
      <MainNavbar />
  
      <Hero />
      <RealValueSection />
      <OpportunitiesSection />
      <PhilosophySection />
      <JourneySection />
      <TransformationSection />
      <ConnectedSection />
      <CategoriesSection />
      <GeneralUserSection />
      <FinalCTA />
      <Footer />
      <button
        type="button"
        className={`sz-advantages-back-to-top${showBackToTop ? ' sz-advantages-back-to-top--visible' : ''}`}
        aria-label="Back to top"
        title="Back to top"
        tabIndex={showBackToTop ? 0 : -1}
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      >
        <ArrowUp size={21} strokeWidth={2.2} aria-hidden="true" />
      </button>
    </div>
  );
}
